import { DocumentNode, OperationVariables, TypedDocumentNode } from '@apollo/client';
import { Stack } from '@mui/material';
import { DataGridProProps, GridRowModel, GridValidRowModel, useGridApiRef, GridApi } from '@mui/x-data-grid-pro';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { RuleGroupType } from '@procureai/query-builder';
import { Widget, WidgetContent } from '@procureai/ui-mui';
import { MutableRefObject, useCallback, useMemo } from 'react';
import { useNavigate, NavigateOptions, To } from 'react-router-dom';
import { Union } from 'ts-toolbelt';

import { Domain } from '~/common/types';
import {
  DataGridCustom,
  DataGridWidgetHeader,
  useDataGridRemoteState,
  DensityButton,
  ColumnSelector,
  ResetRemoteStateButton,
  useDataGridInfiniteLoading,
  useDataGridStatsTranslation,
  useDataGridExport,
  UseDataGridConfig,
  ExportButton,
} from '~/components/DataGridCustom';
import { HoverMenuProvider } from '~/components/HoverMenu';
import { Action, Target, useTrackInteraction } from '~/hooks/useTrackInteraction/useTrackInteraction';

interface UseDataGridInfiniteLoadingProps<TData = any, TVariables extends OperationVariables = OperationVariables> {
  query: DocumentNode | TypedDocumentNode<TData, TVariables>;
  variables?: Omit<TVariables, 'limit' | 'offset'>;
  getNodes: (data: TData) => GridRowModel[];
  getTotalCount: (data: TData) => number | undefined | null;
}

export interface TrackingOptions {
  domain: Domain;
  id: string;
}

export interface DataGridWidgetProps<
  R extends GridValidRowModel = any,
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
  TAdditionalVariables = any,
> {
  apiRef?: MutableRefObject<GridApi>;
  loading?: boolean;
  filter?: RuleGroupType;
  filterCount?: number;
  domain: Domain;
  useConfig: UseDataGridConfig<R>;
  pinnedColumns?: GridInitialStatePro['pinnedColumns'];
  loadingProps: Omit<UseDataGridInfiniteLoadingProps<TData, TVariables>, 'skip' | 'variables'>;
  additionalVariables?: TAdditionalVariables;
  /** will not be updated, always uses the initial value  */
  initialNavigateDetail: (props: {
    row: R;
  }) => Union.Nullable<{ to: To; options?: NavigateOptions; trackingOptions?: TrackingOptions }>;
}

export const DataGridWidget = <
  R extends GridValidRowModel = any,
  TData = any,
  TVariables extends OperationVariables = OperationVariables,
>({
  apiRef: apiRefProp,
  filter,
  filterCount,
  loading: loadingProp,
  domain,
  useConfig,
  loadingProps,
  pinnedColumns,
  initialNavigateDetail,
  additionalVariables,
}: DataGridWidgetProps<R, TData, TVariables>) => {
  const apiRefInternal = useGridApiRef();
  const apiRef = apiRefProp || apiRefInternal;
  const track = useTrackInteraction();

  const navigate = useNavigate();
  const config = useConfig();

  const {
    loading: loadingRemoteState,
    gridProps,
    onDensityChange,
    onRemoteStateReset,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  const initialState = useMemo<GridInitialStatePro | undefined>(() => {
    if (pinnedColumns) {
      return { ...gridProps.initialState, pinnedColumns };
    }
    return gridProps.initialState;
  }, [gridProps.initialState, pinnedColumns]);

  const order = gridProps.sortModel;

  const skip = loadingRemoteState || loadingProp;

  const infiniteLoadingProps = useDataGridInfiniteLoading({
    ...loadingProps,
    // skip loading to avoid fetching the list multiple times
    skip,
    variables: {
      order,
      filter,
      ...additionalVariables,
    } as any,
  });

  const { exportStart, exporting } = useDataGridExport({
    ...loadingProps,
    variables: { order, filter, ...additionalVariables } as any,
    config,
    apiRef,
  });

  const handleRowClick: NonNullable<DataGridProProps['onRowClick']> = useCallback(
    (params) => {
      const nav = initialNavigateDetail(params);

      if (nav) {
        if (nav.trackingOptions) {
          track(Target.DATA_GRID_ROW, Action.CLICKED, nav.trackingOptions);
        }
        navigate(nav.to, nav.options);
      }
    },
    // omitted initialNavigateDetail from deps on purpose because it would change on every render
    // just be aware that it's keeping the initial value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );
  const { rowCount } = infiniteLoadingProps;
  const title = useDataGridStatsTranslation({ rowCount: rowCount || 0, filterCount });

  return (
    <Widget>
      <DataGridWidgetHeader
        title={title}
        right={
          <Stack direction="row">
            <ExportButton loading={exporting} onClick={exportStart} />
            <DensityButton density={gridProps.density} onDensityChange={onDensityChange} />
            <ColumnSelector
              columns={gridProps.columns}
              columnGroupingModel={gridProps.columnGroupingModel}
              columnVisibilityModel={gridProps.columnVisibilityModel}
              onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
            />
            <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
          </Stack>
        }
      />
      <WidgetContent
        disablePadding
        sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', overflow: 'hidden' }}
      >
        <HoverMenuProvider>
          {skip ? null : (
            <DataGridCustom
              sx={{
                // is needed to limit the height to the size of the parent flex-container
                // https://github.com/mui/mui-x/issues/8547
                overflow: 'hidden',
              }}
              apiRef={apiRef}
              onRowClick={handleRowClick}
              paginationMode="server"
              {...infiniteLoadingProps}
              {...gridProps}
              initialState={initialState}
              rowCount={rowCount || 0}
            />
          )}
        </HoverMenuProvider>
      </WidgetContent>
    </Widget>
  );
};
